import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseTestCategory,
  ReleaseTestCategoryStatus,
  ReleaseTestCategoryView,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

const CATEGORIES: ReleaseTestCategory[] = [
  "SMOKE", "FUNCTIONAL", "REGRESSION", "INTEGRATION", "UAT",
  "PERFORMANCE", "SECURITY", "MIGRATION", "COMPATIBILITY",
];

const STATUSES: [ReleaseTestCategoryStatus, string][] = [
  ["NOT_REQUIRED", "Not required"],
  ["NOT_STARTED", "Not started"],
  ["IN_PROGRESS", "In progress"],
  ["PASSED", "Passed"],
  ["FAILED", "Failed"],
  ["BLOCKED", "Blocked"],
  ["WAIVED", "Waived"],
];

/**
 * Which kinds of testing this release needs, and how each went (§28).
 *
 * <p>Required and done are separate questions, which is why both are asked. A release where
 * performance testing was never required reads very differently from one where it was required
 * and nobody ran it, and a single "passed / not passed" column cannot tell them apart.
 */
export function TestCategoryDrawer({
  release,
  category,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  category: ReleaseTestCategoryView | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [which, setWhich] = useState<ReleaseTestCategory>("FUNCTIONAL");
  const [required, setRequired] = useState(true);
  const [status, setStatus] = useState<ReleaseTestCategoryStatus>("NOT_STARTED");
  const [notes, setNotes] = useState("");
  const [waiverReason, setWaiverReason] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setWhich(category?.category ?? "FUNCTIONAL");
    setRequired(category?.required ?? true);
    setStatus(category?.status ?? "NOT_STARTED");
    setNotes(category?.notes ?? "");
    setWaiverReason(category?.waiverReason ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category]);

  const needsWaiver = status === "WAIVED";
  const canSubmit = !needsWaiver || waiverReason.trim() !== "";

  const submit = () =>
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/testing/categories`, {
          category: which,
          required,
          status,
          notes: notes.trim() === "" ? null : notes.trim(),
          waiverReason: waiverReason.trim() === "" ? null : waiverReason.trim(),
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={category ? "Update this kind of testing" : "Add a kind of testing"}
      subtitle="What this release needs tested, and how it went."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <Field label="Kind of testing" required>
          <Select
            value={which}
            disabled={category != null}
            onChange={(event) => setWhich(event.target.value as ReleaseTestCategory)}
          >
            {CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {value.replace(/_/g, " ").toLowerCase()}
              </option>
            ))}
          </Select>
        </Field>

        <label className="flex items-start gap-2 text-sm text-ink-800">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ink-300"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
          />
          <span>
            This release needs it
            <span className="block text-xs text-ink-500">
              Not required is a decision, and it is recorded as one.
            </span>
          </span>
        </label>

        <Field label="How it went">
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value as ReleaseTestCategoryStatus)}
          >
            {STATUSES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        {needsWaiver && (
          <Field label="Why is this being waived?" required>
            <Textarea
              rows={3}
              value={waiverReason}
              onChange={(event) => setWaiverReason(event.target.value)}
              placeholder="No schema change in this release, so data migration testing does not apply."
            />
          </Field>
        )}

        <Field label="Notes">
          <Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}
